import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { DownloadSimple, File, FilePdf, FileImage, FileXls } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getFileIcon = (filename) => {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return FilePdf;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return FileImage;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return FileXls;
  return File;
};

const isViewable = (filename) => {
  const ext = (filename || '').split('.').pop().toLowerCase();
  return ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
};

const isImage = (filename) => {
  const ext = (filename || '').split('.').pop().toLowerCase();
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
};

const DocumentViewer = ({ document, open, onClose, canDownload = true }) => {
  const [imgError, setImgError] = useState(false);

  if (!document) return null;

  const isOpen = open !== undefined ? open : true;

  const fname = document.original_filename || document.filename || '';
  const viewUrl = `${API_URL}/api/documents/download/${document.id}?inline=1`;
  const downloadUrl = `${API_URL}/api/documents/download/${document.id}`;
  const canView = isViewable(fname) && !imgError;
  const isImg = isImage(fname);
  const Icon = getFileIcon(fname);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <div className="flex items-center gap-2 pr-8">
            <Icon size={20} className="text-primary shrink-0" />
            <DialogTitle className="text-sm font-medium truncate flex-1">{fname}</DialogTitle>
            {canDownload && (
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
                <Button variant="outline" size="sm" data-testid="doc-download-btn">
                  <DownloadSimple size={16} className="mr-1" /> Download
                </Button>
              </a>
            )}
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-auto min-h-0 bg-secondary/30">
          {canView ? (
            isImg ? (
              <div className="flex items-center justify-center p-4 min-h-[400px]">
                <img
                  src={viewUrl}
                  alt={fname}
                  className="max-w-full max-h-[70vh] object-contain rounded"
                  onError={() => setImgError(true)}
                  data-testid="doc-image-preview"
                />
              </div>
            ) : (
              <iframe
                src={viewUrl}
                title={fname}
                className="w-full h-[70vh] border-0"
                data-testid="doc-pdf-preview"
              />
            )
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Icon size={48} className="mb-3 opacity-50" />
              <p className="text-sm">Preview not available for this file type</p>
              {canDownload && (
                <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="mt-3">
                  <Button variant="outline" size="sm">
                    <DownloadSimple size={16} className="mr-1" /> Download to view
                  </Button>
                </a>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { DocumentViewer, getFileIcon, isViewable };
